import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Vibration } from 'react-native';
import { HOLD_FALLBACK_SEC, type Exercise } from '@jackedlogs/shared';

import type { DraftEntry } from './state/loggerDraftTypes';
import { isPrescribedBoutSet } from './deriveEntryKind';
import { getLastDurationSecForSlug } from './useEntryHistory';
import { getRestAudioPreference } from './state/restSoundPreference';
import { useCountTimer, type TimerMode } from '../index';
import type { LoggerHostDeps } from './hostDeps';

// Hold-timer orchestration for timed (isometric) exercises — the interactive
// half of inbox 3dd20b1c. Wraps the generic useCountTimer with the
// logger-specific bits: which set is being timed, resolving the per-exercise
// default, and handing the held seconds back on capture. Kept out of
// useLoggerScreen (which is at the 500-line cap) — the screen just instantiates
// this and spreads it into the context both loggers read.
//
// Design (MX-5 slice 3, superseding the 2026-06-05 banner): the timer has no
// surface of its own any more. It runs inside the effort meter's two rows —
// commands + clock on the axis, the live band underneath — and it does NOT
// complete anything. A tap on the band captures the seconds and the effort in
// one gesture, which is what closes AP-0e: the old logHold() wrote the duration
// and called cycleSetPill directly, closing the set mute, with no effort at all.
// So this hook now OWNS the timer and nothing else: `capture()` stops the count
// and RETURNS the seconds, and the effort-commit path writes them in the same
// update_set as the rir. One writer, one dispatch, no second completion road.
// No background cue: the hold is a foreground visual timer (the rest timer owns
// notifee), except for the count-down deadline scheduled below.
export type UseHoldTimer = {
  timer: ReturnType<typeof useCountTimer>;
  /** The set currently being timed, or null when idle. */
  target: { entryIdx: number; setIdx: number } | null;
  /** 3-2-1-GO intro phase before counting starts: 3 | 2 | 1 | 0 (=GO), or
   *  null when no intro is running. The overlay renders the big number. */
  intro: number | null;
  /** Launch (or relaunch) the hold/cardio timer for a set. forceMode overrides
   *  the default-derived direction (the countdown/count-up icons pick it up
   *  front). seedSec seeds a countdown's target from the value already typed
   *  into the entry's duration field (TM-5 point B), falling back to the
   *  per-exercise/global default when absent. */
  start: (
    entryIdx: number,
    setIdx: number,
    forceMode?: TimerMode,
    seedSec?: number,
  ) => void;
  /** Flip count-down ↔ count-up on the running hold. */
  flipDirection: () => void;
  /** The countdown target this entry would resolve to (per-exercise default →
   *  Profile default → 0 = none). Exposed for MX-5's single start chip, which
   *  deduces its DIRECTION from whether a duration exists at all (D6): a
   *  duration ⇒ hourglass counting down, nothing ⇒ stopwatch counting up. The
   *  chip must read the same chain `launch` uses, not a second copy of it. */
  resolveDefaultSec: (entryIdx: number) => number;
  /** Abandon without writing anything. */
  cancel: () => void;
  /** Stop the count and hand back the seconds held (≥1), clearing the timer.
   *  Writes NOTHING and completes NOTHING — the caller folds the seconds into
   *  the effort commit, so a timed set is closed by exactly the same path as
   *  every other set (AP-0e). Returns null — and touches nothing — when no hold
   *  is running OR when the running one belongs to a DIFFERENT set than the one
   *  being logged: the seconds belong to the set that earned them. */
  capture: (entryIdx: number, setIdx: number) => number | null;
};

export function useHoldTimer({
  entries,
  catalog,
  profileDefaultDurationSec,
  timerNotification,
}: {
  entries: DraftEntry[];
  catalog: Exercise[] | null;
  /** Profile.defaultDurationSec — global fallback between the per-exercise
   *  default and HOLD_FALLBACK_SEC. */
  profileDefaultDurationSec: number | undefined;
  timerNotification?: LoggerHostDeps['timerNotification'];
}): UseHoldTimer {
  // Which of the two end-of-count cues the CURRENT count owns. A hold's cue
  // says "tap the band to log the set"; a prescribed bout's says the set is
  // already logged, because it is (HI-1). Set in launch() before the timer
  // starts, so onSchedule below reads the value for the count it is arming.
  const cueKindRef = useRef<'hold' | 'bout'>('hold');

  // Count-down holds schedule a notifee cue at the deadline — the same path
  // (and the same generic timer sound) the rest timer uses, so a weighted
  // plank countdown beeps at 0 even backgrounded. Count-up arms nothing.
  const timer = useCountTimer({
    onSchedule: (deadlineMs) =>
      void timerNotification?.schedule(cueKindRef.current, deadlineMs),
    // Cancels BOTH ids rather than the current kind: cancelling is idempotent
    // and cheap, while reading the ref here would drop the pending cue on the
    // floor any time a second launch flipped the kind between the schedule and
    // the cancel. A trigger nobody cancels fires in the tray minutes later.
    onCancel: () => {
      void timerNotification?.cancel('hold');
      void timerNotification?.cancel('bout');
    },
  });
  const [target, setTarget] = useState<{
    entryIdx: number;
    setIdx: number;
  } | null>(null);
  const [intro, setIntro] = useState<number | null>(null);
  const introRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Remembers the last countdown TARGET (seconds) so a stopwatch→countdown flip
  // on the running banner resumes the value the user was counting down from
  // (e.g. 12:00), not the per-exercise/global default. null/0 ⇒ no prior
  // countdown (or a degenerate 0) ⇒ fall back to the default. Reset per hold in
  // start() so it never leaks across sets.
  const lastCountdownTargetRef = useRef<number | null>(null);

  const clearIntro = useCallback(() => {
    if (introRef.current) {
      clearTimeout(introRef.current);
      introRef.current = null;
    }
    setIntro(null);
  }, []);

  // Stop any pending intro timeout if the screen unmounts mid-countdown.
  useEffect(() => () => clearIntro(), [clearIntro]);

  // Last logged duration → per-exercise default → global Profile default → 0
  // (no target at all ⇒ launch decides stopwatch).
  // ⭐ TM-13 put the athlete's own history FIRST, ahead of the catalog. The
  // catalog number is a stock guess written once for everyone; the history is
  // what this athlete did last time, on this exercise. Antenore's case is the
  // argument: the catalog says 20s for isometric-biceps-hold, his last six
  // sets say 45s, and a countdown seeded at 20 is what sent him to type the
  // real number by hand ([[feedback_derive_thresholds_from_data_not_constants]]).
  // Cold cache ⇒ null ⇒ the chain below answers exactly as it did before.
  const defaultFor = useCallback(
    (entryIdx: number): number => {
      const slug = entries[entryIdx]?.exerciseId;
      const logged = slug ? getLastDurationSecForSlug(slug) : null;
      if (typeof logged === 'number' && logged > 0) return logged;
      const ex = slug ? catalog?.find((x) => x.slug === slug) : undefined;
      const d = ex?.defaultDurationSec;
      if (typeof d === 'number' && d > 0) return d;
      if (
        typeof profileDefaultDurationSec === 'number' &&
        profileDefaultDurationSec > 0
      )
        return profileDefaultDurationSec;
      return 0;
    },
    [entries, catalog, profileDefaultDurationSec],
  );

  // Fire the actual count, no intro. Default mode is the count-UP stopwatch
  // (Antenore 2026-06-05) — most holds are "go as long as you can"; the ⇅
  // toggle flips to a countdown from the resolved per-exercise/global default.
  // A countdown seeds its target from (in order): an explicit seedSec — the
  // value the user already typed into the entry's duration field, so the
  // cardio "start from 10:00" case counts from what's on screen (TM-5, point
  // B) — else the per-exercise/global default, else HOLD_FALLBACK_SEC.
  const launch = useCallback(
    (entryIdx: number, setIdx: number, forceMode?: TimerMode, seedSec?: number) => {
      const def = defaultFor(entryIdx);
      const mode: TimerMode = forceMode ?? 'up';
      const target =
        seedSec && seedSec > 0 ? seedSec : def > 0 ? def : HOLD_FALLBACK_SEC;
      // A countdown remembers its target so a later flip back can resume it.
      if (mode === 'down') lastCountdownTargetRef.current = target;
      const seconds = mode === 'down' ? target : 0;
      // TM-13: the hold countdown MEASURES, so it must be allowed to overrun
      // its own target. Without this the seconds handed back by capture()
      // would be capped at whatever we counted down FROM — a prescription
      // wearing the clothes of a measurement.
      //
      // HI-1 slice 2 is the one case where that is inverted, and the reason is
      // the same rule read from the other side: a prescribed `countdown` bout
      // is BOUNDED, not measured. "30 seconds hard" ends at 30 — the athlete
      // is not being asked how long they lasted, they are being told when to
      // stop — so the deadline is real, remainingSec reaches 0, and the bout
      // closes itself there ([[feedback_prescription_never_becomes_measurement]],
      // interval-training.md §9.1). Read off the draft rather than taken as a
      // parameter: the prescription already lives on the set, and a second way
      // to say it is a second thing that can disagree with the first.
      const bounded =
        mode === 'down' &&
        isPrescribedBoutSet(entries[entryIdx]?.sets[setIdx]);
      cueKindRef.current = bounded ? 'bout' : 'hold';
      timer.start({
        seconds,
        mode,
        tag: `${entryIdx}:${setIdx}`,
        overtime: !bounded,
      });
    },
    [defaultFor, timer, entries],
  );

  // Launch with the 3-2-1-GO intro: 3 → 2 → 1 → GO, then the count begins.
  const start = useCallback(
    (entryIdx: number, setIdx: number, forceMode?: TimerMode, seedSec?: number) => {
      clearIntro();
      timer.reset();
      // Fresh hold ⇒ forget the previous set's countdown target.
      lastCountdownTargetRef.current = null;
      setTarget({ entryIdx, setIdx });
      const tick = (n: number) => {
        setIntro(n);
        // Intro beats (3·2·1·GO) are haptic-only — no audio. The previous
        // foreground playRestSound used react-native-sound, which plays on
        // STREAM_MUSIC (setCategory is iOS-only) and pauses Spotify on every
        // beep — the exact reason the rest timer moved to notifee in 1.97.6
        // ([[feedback_notification_audio_stream]], inbox 9b5daf1a 2026-06-07).
        // notifee can only schedule a future tray notification, not an
        // immediate in-app beep, so the get-ready intro uses vibration only
        // (GO gets a longer buzz). The meaningful audio cue still fires at the
        // hold's end via notifee for count-down holds (scheduleTimerExpiry('hold')).
        const pref = getRestAudioPreference();
        if (pref.vibrate) Vibration.vibrate(n > 0 ? 50 : 150);
        if (n > 0) {
          introRef.current = setTimeout(() => tick(n - 1), 700);
        } else {
          // GO is on screen; brief beat, then start counting.
          introRef.current = setTimeout(() => {
            setIntro(null);
            launch(entryIdx, setIdx, forceMode, seedSec);
          }, 600);
        }
      };
      tick(3);
    },
    [clearIntro, timer, launch],
  );

  // Flip switches direction immediately on the running count — no intro replay.
  const flipDirection = useCallback(() => {
    if (!target) return;
    // TM-8: a flip mid-intro (3-2-1-GO) must win over the intro's own pending
    // relaunch. HoldCountdownOverlay is pointerEvents="none", so the controls
    // underneath stay tappable while the intro plays; without this, the STALE
    // setTimeout from start() fires after this flip and calls launch() with
    // the ORIGINAL forceMode/seedSec, clobbering the flip the user just made.
    clearIntro();
    const next: TimerMode = timer.state.mode === 'down' ? 'up' : 'down';
    // Flipping back to countdown resumes the remembered target (the 12:00 the
    // user was counting down from); a 0/absent memory falls back to the default.
    const remembered = lastCountdownTargetRef.current;
    const seed =
      next === 'down' && remembered && remembered > 0 ? remembered : undefined;
    launch(target.entryIdx, target.setIdx, next, seed);
  }, [target, timer.state.mode, launch, clearIntro]);

  const cancel = useCallback(() => {
    clearIntro();
    timer.reset();
    setTarget(null);
  }, [clearIntro, timer]);

  const capture = useCallback(
    (entryIdx: number, setIdx: number): number | null => {
      // ⚠️ The gate, not a formality. The band tap that captures is the band of
      // the set the athlete is looking at, and that need not be the set being
      // timed: start a plank on one exercise, walk back to another and log a
      // set there, and an ungated capture would hand THAT set the plank's
      // seconds, wipe the running timer and leave the plank pending forever —
      // silently, in one ordinary tap. Found by the a11y/UX review 2026-07-22.
      if (!target || target.entryIdx !== entryIdx || target.setIdx !== setIdx) {
        return null;
      }
      // elapsedSec is the real held time in BOTH modes: count-up counts it,
      // and a count-down started with `overtime` measures it from its own
      // start instant, so holding 45s on a 20s countdown returns 45 and not
      // 20 (TM-13). Clamp to ≥1 so the schema's duration floor passes.
      const secs = Math.max(1, timer.state.elapsedSec);
      clearIntro();
      timer.reset();
      setTarget(null);
      return secs;
    },
    [target, timer, clearIntro],
  );

  return useMemo(
    () => ({
      timer,
      target,
      intro,
      start,
      flipDirection,
      resolveDefaultSec: defaultFor,
      cancel,
      capture,
    }),
    [timer, target, intro, start, flipDirection, defaultFor, cancel, capture],
  );
}
